import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Typography, Stack, Button, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from "@mui/material";
import { COLORS, output } from "../../settings/themeSettings";
import '../../index.scss';

import WestIcon from '@mui/icons-material/West';
import { Update } from '@mui/icons-material';
import { fetchFactureDossier } from "../../data/slices/facturesDossierSlice";
import { fetchCircuit } from "../../data/slices/circuitSlice";
import { PDFViewer } from "@react-pdf/renderer";
import FacturePDF from "../../components/facture-pdf/FacturePDF";
import axiosInstance from "../../config/axiosConfig";
import { PORTEFEUILLE_URL, TO_CLIENT_URL } from "../../settings/APISetting";

const mapFactureDossierToFacture = (fd, fullDossier = null, fullToClient = null) => {
    if (!fd) return null;
    const dossier = fullDossier || fd.dossier;
    
    // Si on a récupéré le fullToClient, on l'utilise, sinon on prend celui du dossier
    const client = fullToClient || dossier?.toClient || dossier?.client || {};
    return {
        ...fd,
        dossier: dossier,
        numeroFacture: fd.numero,
        numeroDossier: dossier?.reference,
        createdAt: fd.date,
        nomClientComptant: dossier?.nomClient || fd.nomClientComptant || '', 
        client: {
            ...client,
            nom: client.libelle || client.nomComplet || client.nom || dossier?.nomClient || '',
            email: fd.emailClientComptant || client.courriel || client.email || '',
            courriel: fd.emailClientComptant || client.courriel || client.email || '',
            adresse: fd.adresseClientComptant || client.adresse || '',
            ville: fd.villeClientComptant || client.ville || '',
            pays: fd.paysClientComptant || client.pays || '',
            telephone: fd.telephoneClientComptant || client.telephone || ''
        },
        billets: [],
        frais: (fd.frais || []).map(f => ({
            ...f,
            libelle: f.designation,
            motif: f.nature,
            montant: (f.prixUnitaire || 0) * (f.quantite || 1)
        }))
    };
};

const DetailsFactureDossier = (props) => {
    const { id, type, dossier } = useParams();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [bankOption, setBankOption] = useState('sgbs');
    const [deviseOption, setDeviseOption] = useState('euro');
    const [facture, setFacture] = useState(null);
    const navigateTo = useNavigate();

    useEffect(() => {
        setLoading(true);
        dispatch(fetchFactureDossier(id)).unwrap()
            .then(async (resp) => {
                output(resp);
                let fullDossier = null;
                let fullToClient = null;
                
                const dossierIsString = typeof resp.dossier === 'string';
                const hasFullToClient = resp.dossier && typeof resp.dossier === 'object' && resp.dossier.toClient && typeof resp.dossier.toClient === 'object' && resp.dossier.toClient.code;
                
                if (resp.dossier && (dossierIsString || !hasFullToClient)) {
                    try {
                        const dossierId = dossierIsString ? resp.dossier.split('/').pop() : resp.dossier.id;
                        const circuitRes = await fetchCircuit(dossierId);
                        fullDossier = circuitRes.data;
                    } catch (e) {
                        output("Failed to fetch full dossier details", e);
                    }
                }

                // Récupérer le vrai toClient avec toutes les infos (adresse, ville, etc.) car Gcircuit les omet
                const dossierSource = fullDossier || resp.dossier;
                if (dossierSource && dossierSource.toClient) {
                    try {
                        const toClientId = typeof dossierSource.toClient === 'string' 
                            ? dossierSource.toClient.split('/').pop() 
                            : dossierSource.toClient.id;
                        
                        if (toClientId) {
                            const url = TO_CLIENT_URL;
                            const clientRes = await axiosInstance.get(`${url}/${toClientId}`);
                            fullToClient = clientRes.data;
                        }
                    } catch(e) {
                        output("Failed to fetch full client details", e);
                    }
                }

                setFacture(mapFactureDossierToFacture(resp, fullDossier, fullToClient));
                setLoading(false);
            })
            .catch((err) => {
                output(err);
                setLoading(false);
            });
    }, [id, dispatch]);

    return <div>
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
        >
            <Stack direction="row" alignItems="center" gap="5" sx={{ flex: 1 }}>
                <div onClick={() => navigateTo(`/dossiers/${type}/${dossier}/factures`)}
                    style={{ background: COLORS.secondary, height: '65px', width: '65px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: `${65 / 2}px`, cursor: 'pointer' }}>
                    <WestIcon style={{ fill: 'white', fontSize: 28 }} />
                </div>
                <Typography variant="h4" sx={{ backgroundColor: COLORS.secondary, color: '#FFF', p: '10px', ml: 5, flex: 1 }}>
                    Facture Dossier (PDF)
                </Typography>
            </Stack>
            
            <Stack direction="row" alignItems="center" spacing={2} sx={{ marginLeft: '20px' }}>
                <Button
                    onClick={() => navigateTo(`/dossiers/${type}/${dossier || facture?.dossier?.id}/factures/${id}/edit`)} 
                    color="primary" 
                    variant="outlined" 
                    startIcon={<Update />}
                    sx={{ backgroundColor: '#fff', mr: 2 }}
                >
                    Modifier
                </Button>
            </Stack>
        </Stack>

        {loading ? (
            <div className="loader-container">
                <span className="loader"></span>
            </div>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh' }}>
                <div style={{ padding: '0 20px 20px 20px' }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>Coordonnées Bancaires à afficher sur la facture :</FormLabel>
                        <RadioGroup
                            row
                            value={bankOption}
                            onChange={(e) => setBankOption(e.target.value)}
                        >
                            <FormControlLabel value="sgbs" control={<Radio color="primary" />} label="SGBS" />
                            <FormControlLabel value="orabank" control={<Radio color="primary" />} label="Orabank" />
                            <FormControlLabel value="none" control={<Radio color="primary" />} label="Cacher la banque" />
                        </RadioGroup>
                    </FormControl>
                    <FormControl component="fieldset" sx={{ ml: 4 }}>
                        <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>Devise de la facture :</FormLabel>
                        <RadioGroup
                            row
                            value={deviseOption}
                            onChange={(e) => setDeviseOption(e.target.value)}
                        >
                            <FormControlLabel value="cfa" control={<Radio color="primary" />} label="Franc CFA" />
                            <FormControlLabel value="euro" control={<Radio color="primary" />} label="Euro" />
                            <FormControlLabel value="both" control={<Radio color="primary" />} label="Les deux" />
                        </RadioGroup>
                    </FormControl>
                </div>
                <PDFViewer style={{ width: '100%', flex: 1 }}>
                    <FacturePDF facture={facture} bankOption={bankOption} deviseOption={deviseOption} />
                </PDFViewer>
            </div>
        )}
    </div>;
}

export default DetailsFactureDossier;

